import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Inbox, MessageCircle, Bot } from 'lucide-react';
import { api, errText } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Banner, Loading, fmtDate } from '@/components/OpsUI';
import { WaTicketDetail } from './WaTicketDetail';
import { WA_STATUS, WA_PRIORITY, WaBadge } from './waTicketMeta';

export function WaTicketsPanel({ canAct, openId, onOpened }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const [current, setCurrent] = useState(openId || null);

  const [debouncedQ, setDebouncedQ] = useState('');
  const reqId = useRef(0);
  useEffect(() => { const t = setTimeout(() => setDebouncedQ(q.trim()), 300); return () => clearTimeout(t); }, [q]);
  const load = useCallback(() => {
    const n = ++reqId.current;
    api.get('/wa-tickets', { params: { status, q: debouncedQ || undefined } })
      .then(({ data }) => { if (n === reqId.current) { setItems(data.data); setError(null); } })
      .catch((e) => { if (n === reqId.current) { setError(errText(e)); setItems([]); } });
  }, [status, debouncedQ]);
  const close = useCallback(() => setCurrent(null), []);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  useEffect(() => { if (openId) { setCurrent(openId); onOpened?.(); } }, [openId, onOpened]);

  return (
    <div className="space-y-4" data-testid="wa-tickets-panel">
      <Card className="p-4 grid grid-cols-1 md:grid-cols-[200px_1fr] gap-3 items-end">
        <div>
          <label className="text-xs text-[hsl(var(--muted-foreground))]">Estado</label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="wa-tickets-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {Object.entries(WA_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Input placeholder="Buscar por número, asunto, nombre, iglesia o teléfono" value={q} onChange={(e) => setQ(e.target.value)} data-testid="wa-tickets-search" />
      </Card>
      {error && <Banner kind="error" title="No se pudieron cargar los tickets de WhatsApp" message={error} />}
      <Card className="overflow-hidden">
        {items === null ? <div className="p-10"><Loading /></div> : items.length === 0 ? (
          <div className="p-12 text-center text-[hsl(var(--muted-foreground))]" data-testid="wa-tickets-empty">
            <Inbox className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p className="font-medium">No hay tickets de WhatsApp</p>
            <p className="text-sm mt-1">El agente IA los crea cuando un caso necesita al equipo técnico, o puedes crearlos desde una conversación.</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {items.map((t) => (
              <button key={t.id} type="button" onClick={() => setCurrent(t.id)} data-testid={`wa-ticket-row-${t.id}`}
                className="w-full text-left p-4 hover:bg-[hsl(var(--muted))]/30 transition-colors flex items-center gap-4">
                <span className="font-mono text-xs text-emerald-300 w-16 shrink-0" data-testid={`wa-ticket-number-${t.id}`}>{t.number}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{t.subject}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5 truncate flex items-center gap-1.5">
                    <MessageCircle className="w-3 h-3 text-emerald-400" />
                    {t.requester?.name || t.requester?.phone} · {t.requester?.church || t.requester?.phone}
                    {t.created_by === 'ai' && <span className="inline-flex items-center gap-1 ml-1 text-[hsl(var(--primary))]"><Bot className="w-3 h-3" />Creado por IA</span>}
                  </div>
                </div>
                <Badge variant="outline" className="hidden md:inline-flex">{t.category}</Badge>
                <WaBadge map={WA_PRIORITY} value={t.priority} />
                <WaBadge map={WA_STATUS} value={t.status} testid={`wa-ticket-status-${t.id}`} />
                <div className="text-xs text-[hsl(var(--muted-foreground))] w-36 text-right hidden lg:block">{fmtDate(t.updated_at)}</div>
              </button>
            ))}
          </div>
        )}
      </Card>
      <WaTicketDetail id={current} canAct={canAct} onClose={close} onChange={load} />
    </div>
  );
}
